<template>
  <main
    class="page"
    role="main"
    aria-labelledby="title"
  >
    <header
      class="page-header"
      aria-labelledby="title"
    >
      <h1
        id="title"
        class="h1"
      >
        Logbuch
      </h1>
    </header>

    <section class="card">
      <div
        class="toolbar"
        style="display:flex; gap:12px; align-items:flex-end; flex-wrap:wrap;"
      >
        <label
          class="field"
          style="min-width:260px; flex:1 1 300px;"
        >
          <span class="label">Nutzer</span>
          <select
            v-model="filters.userId"
            class="input"
            style="height:38px;"
          >
            <option value="">Alle Nutzer</option>
            <option
              v-for="u in usersSorted"
              :key="u.id"
              :value="String(u.id)"
            >
              {{ formatName(u) }}
            </option>
          </select>
        </label>

        <label
          class="field"
          style="min-width:200px;"
        >
          <span class="label">Bereich</span>
          <select
            v-model="filters.area"
            class="input"
            style="height:38px;"
          >
            <option
              v-for="a in areaOptions"
              :key="a.value || 'all'"
              :value="a.value"
            >
              {{ a.label }}
            </option>
          </select>
        </label>

        <label
          class="field"
          style="min-width:160px;"
        >
          <span class="label">Typ</span>
          <select
            v-model="filters.context"
            class="input"
            style="height:38px;"
          >
            <option value="">Alle</option>
            <option value="add">Anlegen</option>
            <option value="edit">Bearbeiten</option>
            <option value="delete">Löschen</option>
          </select>
        </label>

        <label class="field date-picker">
          <span class="label">Datum von</span>
          <flat-pickr
            v-model="filters.dateFrom"
            :config="fpOpts"
            class="input"
          />
        </label>

        <label class="field date-picker">
          <span class="label">Datum bis</span>
          <flat-pickr
            v-model="filters.dateTo"
            :config="fpOpts"
            class="input"
          />
        </label>

        <button
          class="btn btn-primary field"
          type="button"
          :disabled="loading"
          @click="load"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          Anzeigen
        </button>
        <button
          class="btn field"
          type="button"
          :disabled="loading"
          @click="reset"
        >
          Reset
        </button>
      </div>

      <div
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </div>

      <div
        v-else
        class="timeline-list"
        style="margin-top:12px; max-height:calc(100vh - 290px); overflow:auto;"
      >
        <div
          v-for="item in logs"
          :key="item.id"
          class="timeline-item"
          style="display:grid; grid-template-columns: 160px 1fr; gap:12px; padding:10px 0; border-bottom:1px solid var(--border);"
        >
          <div
            class="meta"
            style="color:var(--muted); font-size:12px; line-height:18px;"
          >
            <div>{{ formatDateTime(item.created_at) }}</div>
            <div
              v-if="item.user?.name || item.user?.email"
              style="margin-top:2px;"
            >
              <i
                class="pi pi-user"
                aria-hidden="true"
              />
              <span style="margin-left:6px;">{{ item.user?.name || item.user?.email }}</span>
            </div>
          </div>
          <div class="content">
            <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
              <span
                class="badge"
                :data-context="item.context"
              >{{ item.context }}</span>
              <span
                class="badge"
                style="background:rgba(37,99,235,.08); color:#1e3a8a;"
              >{{ prettyArea(item.area) }}</span>
              <span
                v-if="item.path"
                class="muted"
                style="font-size:12px;"
              >{{ item.path }}</span>
            </div>
            <div style="margin-top:6px;">
              {{ summarize(item) }}
            </div>
            <details
              v-if="hasDetails(item)"
              style="margin-top:6px;"
            >
              <summary style="cursor:pointer;">
                Details
              </summary>
              <pre style="font-size:12px; background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:8px; overflow:auto;">{{ prettyJson(item.changes) }}</pre>
            </details>
          </div>
        </div>

        <div
          v-if="!loading && logs.length === 0"
          class="muted"
          style="padding:8px 0;"
        >
          Keine Einträge gefunden.
        </div>
      </div>
    </section>
    <teleport to=".top-nav-help-slot">
      <span
        id="top-nav-help-change-logs"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Logbuch">
          <ul>
            <li>Filter: Nutzer, Bereich, Typ sowie Datumsbereich (von/bis). „Anzeigen“ lädt entsprechende Einträge.</li>
            <li>Die Liste zeigt pro Eintrag Zeitpunkt, Nutzer, Aktion (anlegen/bearbeiten/löschen) und Bereich.</li>
            <li>„Details“ öffnet die technische Änderung als JSON – nur bei Bedarf ansehen.</li>
            <li>„Reset“ stellt die Standardfilter (heute) wieder her.</li>
            <li>Hinweis: Bereiche sind deutsch beschriftet (z. B. „Angebote/Termine“).</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {onMounted, ref, computed} from 'vue';
import {adminApi} from '@/services/api/admin';
import usersApi from '@/api/users';
import UiInfoButton from '@/components/UiInfoButton.vue';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const fpOpts = {
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: German,
  disableMobile: true
};

const today = new Date().toISOString().slice(0, 10);

// Dropdown-Optionen für Bereiche (deutsche Labels)
const areaOptions = [
  { value: '', label: 'Alle Bereiche' },
  { value: 'students', label: 'Schüler' },
  { value: 'groups', label: 'Gruppen' },
  { value: 'users', label: 'Benutzer' },
  { value: 'events', label: 'Angebote/Termine' },
  { value: 'school-years', label: 'Schuljahre' },
  { value: 'attendance', label: 'Anwesenheit' },
  { value: 'presence', label: 'Präsenz' },
  { value: 'notifications', label: 'Meldungen' },
  { value: 'auth', label: 'Auth' },
];

const filters = ref({
  userId: '',
  area: '',
  context: '',
  dateFrom: today,
  dateTo: today,
});

const logs = ref([]);
const loading = ref(false);
const error = ref(null);

// Benutzerliste für Dropdown
const users = ref([]);

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
}

function formatName(u) {
  const last = (u.lastName || u.lastname || '').trim();
  const first = (u.firstName || u.firstname || '').trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || (u.displayName || u.email || '').trim() || `#${u.id}`;
}

const usersSorted = computed(() => {
  return extractItems(users.value)
    .slice()
    .sort((a, b) => formatName(a).localeCompare(formatName(b), 'de'));
});

async function loadUsers() {
  try {
    const res = await usersApi.list({ limit: 1000 });
    users.value = extractItems(res?.data);
  } catch {
    users.value = [];
  }
}

function reset() {
  filters.value = {
    userId: '',
    area: '',
    context: '',
    dateFrom: today,
    dateTo: today,
  };
  load();
}

function formatDateTime(ts) {
  try {
    const d = new Date(ts);
    const dd = d.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
    const tt = d.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'});
    return `${dd} ${tt} Uhr`;
  } catch {
    return String(ts);
  }
}

function prettyArea(area) {
  const map = {
    students: 'Schüler',
    groups: 'Gruppen',
    users: 'Benutzer',
    events: 'Angebote/Termine',
    'school-years': 'Schuljahre',
    attendance: 'Anwesenheit',
    presence: 'Präsenz',
    notifications: 'Meldungen',
    auth: 'Auth',
    api: 'API'
  };
  return map[area] || area;
}

function parsePath(path) {
  if (!path) return {};
  const seg = String(path).split('?')[0].split('/').filter(Boolean);
  const apiIdx = seg.indexOf('api');
  const entity = apiIdx >= 0 && seg[apiIdx + 1] ? seg[apiIdx + 1] : null;
  const id = apiIdx >= 0 && seg[apiIdx + 2] && /^\d+$/.test(seg[apiIdx + 2]) ? Number(seg[apiIdx + 2]) : null;
  const action = seg[seg.length - 1] && isNaN(Number(seg[seg.length - 1])) ? seg[seg.length - 1] : null;
  return {entity, id, action};
}


function summarize(item) {
  const ctx = String(item.context || '').toLowerCase();
  const {entity, id, action} = parsePath(item.path || '');
  const entityLabel = prettyArea(entity || item.area);

  // Name aus changes_json priorisieren, ID als Fallback
  const nameFromChanges =
    item && item.changes && typeof item.changes === 'object'
      ? (item.changes._entity_name || (item.changes._entity && item.changes._entity.name) || item.changes.name ||null)
      : null;
  const instanceText = nameFromChanges ? ` ${nameFromChanges}` : (id ? ` #${id}` : '');

  if (ctx === 'add') {
    return `Angelegt: ${instanceText}.`;
  }
  if (ctx === 'delete') {
    return `Gelöscht: ${instanceText}.`;
  }
  // edit oder sonstiges
  if (action && action !== String(id || '')) {
    return `Aktion „${action}“ auf ${entityLabel}${instanceText}.`;
  }
  return `Geändert: ${instanceText}.`;
}

function hasDetails(item) {
  return !!item?.changes && typeof item.changes === 'object';
}

function prettyJson(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const params = {
      userId: filters.value.userId || undefined,
      area: filters.value.area || undefined,
      context: filters.value.context || undefined,
      dateFrom: filters.value.dateFrom,
      dateTo: filters.value.dateTo,
      page: 1,
      perPage: 200
    };
    const res = await adminApi.getChangeLogs(params);
    logs.value = extractItems(res?.data);
  } catch (e) {
    error.value = 'Laden fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadUsers();
  load();
});
</script>

<style scoped>
.page {
  padding: 0;
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.h1 {
  font-size: 26px;
  line-height: 38px;
  font-weight: 600;
  margin: 0;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 0;
  min-height: calc(100vh - 160px);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  font-size: 12px;
  color: var(--muted);
}

.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.input:focus {
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
  outline: none;
}

.btn {
  height: 38px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
  border-color: var(--primary-600);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 6px;
  vertical-align: -3px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(0,0,0,.06);
}

.badge[data-context="add"] {
  background: rgba(16, 185, 129, .12);
  color: #065f46;
}
.badge[data-context="edit"] {
  background: rgba(37, 99, 235, .12);
  color: #1e3a8a;
}
.badge[data-context="delete"] {
  background: rgba(239, 68, 68, .12);
  color: #991b1b;
}

.muted {
  color: var(--muted);
}
</style>
